/**
 * 指挥页地图飞行 / 最近活跃队员定位（从 Dashboard 拆出）
 */

import { toMapLngLat } from './basemapCoord';
import { isBogusGpsCoordinate, isPlausibleCommandCoordinate } from '../bogusGps';

export function getMemberLngLat(member) {
  if (!member) return null;
  const lng = Number(member.last_longitude ?? member.longitude ?? member.lng);
  const lat = Number(member.last_latitude ?? member.latitude ?? member.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  if (isBogusGpsCoordinate(lat, lng)) return null;
  return [lng, lat];
}

/** 取队员最近上报时间（设备列表字段为 last_seen，兼容旧 device_time） */
export function getMemberReportTime(member) {
  if (!member) return null;
  const raw = member.last_seen || member.device_time || member.updated_at;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

/** 有坐标且有上报时间的最近活跃队员 */
export function findLatestActiveMember(members = []) {
  if (!members?.length) return null;
  const withLocation = members.filter(
    (member) => getMemberLngLat(member) && getMemberReportTime(member) != null
  );
  if (withLocation.length === 0) return null;
  return [...withLocation].sort(
    (a, b) => getMemberReportTime(b) - getMemberReportTime(a)
  )[0];
}

/**
 * 从队员构造「最后一次设备定位记录」
 * @returns {{ lng:number, lat:number, name:string, deviceUniqueId:string, reportedAt:number|null, reportedAtRaw:string|null }|null}
 */
export function buildLastDeviceLocationRecord(member) {
  const center = getMemberLngLat(member);
  if (!center) return null;
  return {
    lng: center[0],
    lat: center[1],
    name: member?.name || '未知设备',
    deviceUniqueId: member?.device_unique_id || member?.name || '',
    reportedAt: getMemberReportTime(member),
    reportedAtRaw: member?.last_seen || member?.device_time || member?.updated_at || null
  };
}

/** 从队员列表解析最新一次设备定位记录 */
export function resolveLatestDeviceLocationRecord(members = []) {
  return buildLastDeviceLocationRecord(findLatestActiveMember(members));
}

/**
 * 用新列表刷新「最后定位记录」：仅当新记录更新（或此前为空）时覆盖
 */
export function rememberLastDeviceLocation(prevRecord, members = []) {
  const next = resolveLatestDeviceLocationRecord(members);
  if (!next) return prevRecord || null;
  if (!prevRecord) return next;
  if ((next.reportedAt || 0) >= (prevRecord.reportedAt || 0)) return next;
  return prevRecord;
}

const LAST_DEVICE_LOCATION_KEY = 'dashboard_last_device_location';

/** 读「上次队员设备定位」：用 localStorage，刷新后同一浏览器仍可恢复 */
export function readLastDeviceLocationFromStorage(
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) {
  if (!storage) return null;
  try {
    // 兼容旧版写在 sessionStorage 的记录
    let raw = storage.getItem(LAST_DEVICE_LOCATION_KEY);
    if (!raw && typeof sessionStorage !== 'undefined') {
      raw = sessionStorage.getItem(LAST_DEVICE_LOCATION_KEY);
      if (raw) {
        try {
          storage.setItem(LAST_DEVICE_LOCATION_KEY, raw);
          sessionStorage.removeItem(LAST_DEVICE_LOCATION_KEY);
        } catch (_) {
          /* ignore */
        }
      }
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(Number(parsed?.lng)) || !Number.isFinite(Number(parsed?.lat))) return null;
    if (!isPlausibleCommandCoordinate(parsed.lat, parsed.lng)) {
      storage.removeItem(LAST_DEVICE_LOCATION_KEY);
      console.warn('忽略境外/伪定位设备存档', parsed.lat, parsed.lng);
      return null;
    }
    return {
      ...parsed,
      lng: Number(parsed.lng),
      lat: Number(parsed.lat),
      reportedAt: Number.isFinite(Number(parsed.reportedAt)) ? Number(parsed.reportedAt) : null
    };
  } catch (_) {
    return null;
  }
}

export function writeLastDeviceLocationToStorage(
  record,
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) {
  if (!storage || !record) return;
  if (!isPlausibleCommandCoordinate(record.lat, record.lng)) {
    console.warn('拒绝写入境外/伪定位设备坐标', record.lat, record.lng);
    try {
      storage.removeItem(LAST_DEVICE_LOCATION_KEY);
    } catch (_) {
      /* ignore */
    }
    return;
  }
  try {
    storage.setItem(LAST_DEVICE_LOCATION_KEY, JSON.stringify({
      ...record,
      lng: Number(record.lng),
      lat: Number(record.lat),
      savedAt: Date.now()
    }));
  } catch (_) {
    /* ignore */
  }
}

/** 飞到最后一次设备定位记录坐标（按坐标飞行，不依赖实时队员对象） */
export function flyToLastDeviceLocationRecord(map, record, options = {}) {
  if (!map || !record) return false;
  const lng = Number(record.lng);
  const lat = Number(record.lat);
  if (!isPlausibleCommandCoordinate(lat, lng)) return false;

  const basemapId = options.basemapId;
  const display = toMapLngLat(lng, lat, basemapId, map) || [lng, lat];
  const screenH = map.getContainer?.()?.clientHeight
    || (typeof window !== 'undefined' ? window.innerHeight : 800);
  const zoom = options.zoom ?? calculateZoomForHeight(
    options.heightMeters ?? COMMAND_VIEW_HEIGHT_METERS,
    lat,
    screenH
  );

  try {
    map.stop?.();
  } catch (_) {
    /* ignore */
  }

  map.flyTo({
    center: display,
    zoom,
    duration: options.duration ?? 3200,
    essential: true,
    ...(options.easing ? { easing: options.easing } : { easing: (t) => t * (2 - t) })
  });
  return true;
}

/**
 * 按目标离地高度估算缩放级别（与 estimateViewHeight 互逆）
 * 默认约 600m（龙泉指挥视野常用高度）
 */
export const COMMAND_VIEW_HEIGHT_METERS = 600;

export function calculateZoomForHeight(heightMeters = COMMAND_VIEW_HEIGHT_METERS, latitude = 30, screenHeightPx = 800) {
  const h = Math.max(50, Number(heightMeters) || 600);
  const sh = Math.max(400, Number(screenHeightPx) || (
    (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 800
  ));
  // estimateViewHeight: height ≈ (initialResolution / 2^zoom * screenHeight) / 2
  const R = 6378137;
  const initialResolution = (2 * Math.PI * R) / 256;
  // 纬度修正：高纬同样 zoom 对应的地面分辨率更小
  const latRad = (Number(latitude) || 30) * Math.PI / 180;
  const cosLat = Math.max(0.2, Math.cos(latRad));
  const zoom = Math.log2((initialResolution * cosLat * sh) / (2 * h));
  const targetZoom = Math.min(20, Math.max(1, zoom));
  console.log(
    `计算缩放级别: 纬度=${latitude}, 目标高度=${h}m, 最终缩放=${targetZoom.toFixed(2)}`
  );
  return targetZoom;
}

/**
 * @returns {boolean} 是否成功发起飞行
 */
export function flyToMemberOnMap(map, member, options = {}) {
  const {
    heightMeters = 600,
    duration = 2800,
    speed = 1.2,
    curve = 1.42,
    zoom,
    beforeFly,
    basemapId
  } = options;

  const wgs = getMemberLngLat(member);
  if (!map || !wgs) return false;

  beforeFly?.();
  try {
    map.stop?.();
  } catch (_) {
    /* ignore */
  }

  // 业务 WGS84 → 当前底图显示坐标（高德/谷歌 GCJ-02；Mapbox 原样）
  const center = toMapLngLat(wgs[0], wgs[1], basemapId, map) || wgs;

  const targetZoom = zoom != null
    ? zoom
    : calculateZoomForHeight(heightMeters, wgs[1]);

  map.flyTo({
    center,
    zoom: targetZoom,
    speed,
    curve,
    essential: true,
    duration,
    ...(options.easing ? { easing: options.easing } : {})
  });
  return true;
}

export function flyToLngLat(map, lng, lat, options = {}) {
  if (!map || !Number.isFinite(Number(lng)) || !Number.isFinite(Number(lat))) return false;
  try {
    map.stop?.();
  } catch (_) {
    /* ignore */
  }
  // 业务 WGS84 → 当前底图显示坐标（高德/谷歌需 GCJ-02）
  const center = toMapLngLat(lng, lat, options.basemapId, map) || [Number(lng), Number(lat)];
  map.flyTo({
    center,
    zoom: options.zoom ?? 15,
    duration: options.duration ?? 3000,
    essential: true,
    ...(options.easing ? { easing: options.easing } : {})
  });
  return true;
}
